import type * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IStyle, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { concatStyleSets, memoizeFunction, mergeStyles } from "@fluentui/react";

import type { AppColors } from "@1js/acui-common";
import { getPrimaryControlSemanticColorsWithTheme } from "@1js/acui-common";

import { SelectedPivotIndicatorDirection } from "./SelectedPivotIndicator.Props";
import { customTokens, isNextStyleEnabled } from "@1js/acui-theme";

export interface SelectedPivotIndicatorStyles {
  root?: IStyle;
}

export interface SelectedPivotIndicatorClassNames {
  root: string;
}

const getPivotColor = memoizeFunction(
  (
    theme: ITheme,
    appColors?: AppColors,
    nextStylesEnabled?: boolean
  ): string | undefined => {
    const actionControlSemanticColors =
      getPrimaryControlSemanticColorsWithTheme(theme, appColors);
    return nextStylesEnabled
      ? customTokens.colorCompoundBrandStroke
      : actionControlSemanticColors.primaryTextColor;
  }
);

export const getSelectedPivotIndicatorStyles = memoizeFunction(
  (
    position: number,
    offset: number,
    length: number,
    thickness: number,
    showAnimation: boolean,
    direction: SelectedPivotIndicatorDirection,
    theme: ITheme,
    appColors?: AppColors,
    styles?: SelectedPivotIndicatorStyles,
    useNextStyles?: boolean,
    isTabIndicatorRounded?: boolean
  ): SelectedPivotIndicatorStyles => {
    const vertical = direction === SelectedPivotIndicatorDirection.Vertical;
    const nextStylesEnabled = useNextStyles && isNextStyleEnabled();
    const backgroundColor = getPivotColor(theme, appColors, nextStylesEnabled);
    const borderRadius =
      nextStylesEnabled || isTabIndicatorRounded
        ? customTokens.borderRadiusCircular
        : 0;
    return concatStyleSets(
      {
        root: {
          margin: 0,
          top: vertical ? position : -1 * (thickness + offset),
          left: vertical ? offset : position,
          width: vertical
            ? nextStylesEnabled
              ? customTokens.strokeWidthThick
              : thickness
            : length,
          height: vertical
            ? length
            : nextStylesEnabled
              ? customTokens.strokeWidthThick
              : thickness,
          pointerEvents: "none",
          position: "relative",
          transition: showAnimation
            ? "all 400ms cubic-bezier(0.17, 0.17, 0.00, 1.00)"
            : undefined,
          float: "left",
          backgroundColor,
          borderRadius,
        },
      },
      styles
    );
  }
);

export const getPlaceHolderStyles = memoizeFunction(
  (
    theme: ITheme,
    appColors?: AppColors,
    customStyles?: React.CSSProperties,
    useNextStyles?: boolean
  ): React.CSSProperties => {
    const nextStylesEnabled = useNextStyles && isNextStyleEnabled();
    const horizontalMargin = nextStylesEnabled ? 11 : 15;
    return {
      height: nextStylesEnabled ? customTokens.strokeWidthThick : 3,
      margin: `0px ${horizontalMargin}px`,
      top: -3,
      width: `calc(100% - ${horizontalMargin * 2}px)`,
      backgroundColor: getPivotColor(theme, appColors, nextStylesEnabled),
      position: "relative",
      ...customStyles,
    };
  }
);

export const getSelectedPivotIndicatorClassNames = memoizeFunction(
  (styles: SelectedPivotIndicatorStyles): SelectedPivotIndicatorClassNames => {
    return {
      root: mergeStyles(styles.root),
    };
  }
);
